
import { BaseStyles } from './BaseStyles';
import { TailwindProcessor } from './TailwindProcessor';
import type { StyleManagerConfig } from './types/StyleManagerTypes';

export class StyleInitializer {
  private shadow: ShadowRoot;
  private config: StyleManagerConfig;
  
  constructor(shadow: ShadowRoot, config: StyleManagerConfig) {
    this.shadow = shadow;
    this.config = config;
  }
  
  initializeStyles(): void {
    console.log('[DEBUG] StyleInitializer: Starting style initialization');
    
    // Add styles in order of specificity
    this.addScopingStyles();    // 1. First add scoping to ensure proper containment
    this.addBaseStyles();       // 2. Then base styles
    this.addEssentialStyles(); // 3. Then essential component styles
    this.addWordPressStyles(); // 4. Finally WordPress-specific overrides
    
    console.log('[DEBUG] StyleInitializer: All styles initialized');
  }
  
  private addBaseStyles(): void {
    try {
      const baseStyle = document.createElement('style');
      baseStyle.textContent = `
        /* Ensure styles are scoped to our container */
        :host {
          display: block;
          width: 100%;
          max-width: 100%;
        }
        
        ${BaseStyles.getBaseStylesContent()}
      `;
      this.shadow.appendChild(baseStyle);
      console.log('[DEBUG] Base styles added with length:', baseStyle.textContent.length);
    } catch (error) {
      console.error('[ERROR] Error adding base styles:', error);
    }
  }
  
  private addScopingStyles(): void {
    try {
      const scopingStyles = TailwindProcessor.createScopingStyles();
      this.shadow.appendChild(scopingStyles);
      console.log('[DEBUG] Scoping styles added');
    } catch (error) {
      console.error('[ERROR] Error adding scoping styles:', error);
    }
  }
  
  private addEssentialStyles(): void {
    try {
      const essentialStyles = TailwindProcessor.createEssentialStyles();
      this.shadow.appendChild(essentialStyles);
      console.log('[DEBUG] Essential styles added');
    } catch (error) {
      console.error('[ERROR] Error adding essential styles:', error);
    }
  }
  
  // Add the missing createFallbackStyles method
  createFallbackStyles(): HTMLStyleElement {
    return TailwindProcessor.createFallbackStyles();
  }
  
  private addWordPressStyles(): void {
    try {
      const wpStyles = document.createElement('style');
      wpStyles.textContent = `
        /* WordPress-specific resets and overrides */
        .onexip-calculator-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
          line-height: 1.5 !important;
          color: #0f172a !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        /* Reset WordPress theme interference */
        .onexip-calculator-container * {
          font-family: inherit !important;
          line-height: inherit !important;
          box-sizing: border-box !important;
        }
        
        /* Ensure buttons look consistent */
        .onexip-calculator-container button {
          border: none !important;
          background: none !important;
          padding: revert !important;
          margin: revert !important;
          line-height: revert !important;
          min-height: revert !important;
          height: revert !important;
        }
        
        /* Fix heading styles */
        .onexip-calculator-container h1,
        .onexip-calculator-container h2,
        .onexip-calculator-container h3,
        .onexip-calculator-container h4,
        .onexip-calculator-container h5,
        .onexip-calculator-container h6 {
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          font-weight: 600 !important;
        }
        
        /* Ensure proper spacing */
        .onexip-calculator-container .max-w-4xl {
          max-width: 56rem !important;
        }
        
        .onexip-calculator-container .px-4 {
          padding-left: 1rem !important;
          padding-right: 1rem !important;
        }
        
        .onexip-calculator-container .py-8 {
          padding-top: 2rem !important;
          padding-bottom: 2rem !important;
        }
        
        /* Force our colors */
        .onexip-calculator-container .bg-primary {
          background-color: hsl(var(--primary)) !important;
        }
        
        .onexip-calculator-container .text-primary {
          color: hsl(var(--primary)) !important;
        }
      `;
      this.shadow.appendChild(wpStyles);
      console.log('[DEBUG] WordPress compatibility styles added');
    } catch (error) {
      console.error('[ERROR] Error adding WordPress styles:', error);
    }
  }
}
